'use client'

import { useState } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { ExternalLink, LifeBuoy, Workflow } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import {
    PaymentFlowItemWithProcessors,
    usePaymentFlowsListWithProcessors,
} from '@/lib/api/paymentFlows'
import { useStoreRescueState } from '@/lib/api/cryptoRescue'
import { Button } from '@/components/ui/button'
import {
    PaymentFlowsLoading,
    PaymentFlowCard,
    PaymentFlowDeleteDialog,
    PaymentFlowDuplicateDialog,
} from './components'
import { Store, useStore } from '@/lib/api/stores'
import { useOrgNavigate } from '@/hooks/use-app-navigate'

const PaymentFlowsListPageInner = () => {
    const { t } = useTranslation(['paymentFlows', 'stores', 'common'])
    const { id: storeId } = useParams()
    if (!storeId) {
        throw new Error('Store ID is required')
    }
    const {
        data: response,
        isLoading,
        isError,
    } = usePaymentFlowsListWithProcessors()
    const { data: store, isLoading: isLoadingStore, isError: isErrorStore } = useStore(storeId)
    const { data: rescueState } = useStoreRescueState(storeId, !!storeId)
    // Gate banner on both `configured` + `active` — without the env-driven
    // fallback the feature is platform-disabled regardless of stored state.
    const isRescueActive = rescueState?.configured === true && rescueState?.active === true

    if (isLoading || isLoadingStore) return <PaymentFlowsLoading />
    if (isError || isErrorStore) return <div>{t('paymentFlows:error')}</div>
    if (!store) return <div>{t('paymentFlows:store_not_found')}</div>

    return (
        <>
            {isRescueActive && (
                <div className="mb-4 rounded-md border-2 border-red-500/70 bg-red-50/30 px-3 py-2 text-sm text-red-700 dark:bg-red-950/10 dark:text-red-400">
                    <div className="flex items-start gap-2">
                        <LifeBuoy className="size-4 mt-0.5 shrink-0" />
                        <span>
                            {t('stores:paymentFlows.rescueBanner')}
                        </span>
                    </div>
                </div>
            )}
            <PaymentFlowContent paymentFlows={response?.flows ?? []} store={store ?? null} />
        </>
    )
}

interface PaymentFlowContentProps {
    paymentFlows: PaymentFlowItemWithProcessors[]
    store: Store
}

const PaymentFlowContent = ({ paymentFlows, store }: PaymentFlowContentProps) => {
    const { t } = useTranslation(['paymentFlows', 'common'])
    const [flowToDelete, setFlowToDelete] = useState<PaymentFlowItemWithProcessors | null>(null)
    const [flowToDuplicate, setFlowToDuplicate] = useState<PaymentFlowItemWithProcessors | null>(null)
    const { orgRoutes } = useOrgNavigate()

    const handleDelete = (flow: PaymentFlowItemWithProcessors) => {
        setFlowToDelete(flow)
    }

    const handleDuplicate = (flow: PaymentFlowItemWithProcessors) => {
        setFlowToDuplicate(flow)
    }

    if (paymentFlows.length === 0) {
        return <EmptyState />
    }

    // Sort flows: default flow first, then alphabetically
    const sortedFlows = [...paymentFlows].sort((a, b) => {
        if (a.id === store.selectedPaymentFlowId) return -1;
        if (b.id === store.selectedPaymentFlowId) return 1;
        return a.name.localeCompare(b.name);
    });

    return (
        <>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">
                            {t('paymentFlows:store_payment_flow')}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('paymentFlows:store_payment_flow_description')}
                        </p>
                    </div>
                    <Button variant="outline" asChild>
                        <Link to={orgRoutes.PAYMENT_FLOWS()}>
                            <ExternalLink className="mr-2 size-4" />
                            {t('paymentFlows:manage_flows')}
                        </Link>
                    </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {sortedFlows.map((flow) => (
                        <PaymentFlowCard
                            store={store}
                            key={flow.id}
                            flow={flow}
                            onDelete={handleDelete}
                            onDuplicate={handleDuplicate}
                        />
                    ))}
                </div>
            </div>

            <PaymentFlowDeleteDialog
                flow={flowToDelete}
                open={!!flowToDelete}
                onOpenChange={(open) => !open && setFlowToDelete(null)}
                onClose={() => setFlowToDelete(null)}
            />

            <PaymentFlowDuplicateDialog
                flow={flowToDuplicate}
                open={!!flowToDuplicate}
                onOpenChange={(open) => !open && setFlowToDuplicate(null)}
            />
        </>
    )
}

const EmptyState = () => {
    const { t } = useTranslation(['paymentFlows', 'common'])
    const { orgRoutes } = useOrgNavigate()

    return (
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed p-12">
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
                <Workflow className="size-8 text-primary" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">
                {t('paymentFlows:empty_state.title')}
            </h3>
            <p className="mb-6 max-w-md text-center text-muted-foreground">
                {t('paymentFlows:no_flows_available')}
            </p>
            <Button asChild>
                <Link to={orgRoutes.PAYMENT_FLOWS()}>
                    <ExternalLink className="mr-2 size-4" />
                    {t('paymentFlows:go_to_payment_flows')}
                </Link>
            </Button>
        </div>
    )
}

export const PaymentFlowsListPage = withRequiredPermission(PaymentFlowsListPageInner, PERMISSIONS.STORES_VIEW)
